import { type ChangeEvent, type DragEvent, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, PaletteUtils } from '@shared/utils';
import { Button, Icon, Text } from '../index';
import AttachmentIcon from '@assets/icons/attachment.svg';
import CrossIcon from '@assets/icons/cross.svg';

const ACCEPTED_FILE_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.heic', '.heif', '.pdf', '.docx', '.doc', '.xlsx', '.xls', '.odt', '.ods'];

export interface CustomFile {
  id: string;
  name: string;
  file: string | Blob;
  type: string;
}
export interface FileWithId extends File {
  id?: string;
}

interface BaseProps {
  className?: string;
  labelClassName?: string;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  onDelete?: (file: FileWithId | CustomFile) => void;
}
interface SingleProps extends BaseProps {
  multiple?: false;
  value?: FileWithId | CustomFile | null;
  onChange?: (file: FileWithId | CustomFile) => void;
}
interface MultipleProps extends BaseProps {
  multiple: true;
  value?: (FileWithId | CustomFile)[];
  onChange?: (files: (FileWithId | CustomFile)[]) => void;
}
type FileUploadProps = SingleProps | MultipleProps;

export const FileUpload = (props: FileUploadProps) => {
  const id = useId();

  const { t } = useTranslation();

  const addFiles = (files: File[]) => {
    if (props.multiple) {
      const existingFiles = props.value ?? [];
      const newFiles = files.filter((file) => !existingFiles.some((f) => f.name === file.name && f.type === file.type));
      props.onChange?.([...existingFiles, ...newFiles]);
    } else props.onChange?.(files[0] ?? null);
  };

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const dropHandler = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (props.disabled) return;
    addFiles(Array.from(e.dataTransfer.files));
  };

  const hasFiles = props.multiple ? (props.value?.length ?? 0) > 0 : !!props.value;
  return (
    <div className={cn(props.className)}>
      {props.label && (
        <label htmlFor={id} className={cn('w-full', props.labelClassName)}>
          <Text size='sm' color='secondary'>
            {props.label} {props.required ? ' *' : ''}
          </Text>
        </label>
      )}
      {(props.multiple || !props.value) && (
        <label
          htmlFor={id}
          className={cn(
            'flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-dark-wet-asphalt p-3 transition-colors duration-200',
            PaletteUtils.isInsigniaPalette && [
              'hover:bg-smoky-white dark:hover:bg-signal-dark-black',
              'active:bg-gray-gainsborough dark:active:bg-white-signal-black',
            ],
            PaletteUtils.isVcfcPalette && [
              'bg-white dark:bg-light/5',
              'hover:bg-gainsborough dark:hover:bg-dark-wet-asphalt',
              'active:bg-light dark:active:bg-signal-dark-black',
            ],
            props.disabled && 'border-gray-300 cursor-not-allowed',
          )}
          onDrop={dropHandler}
          onDragOver={(e) => e.preventDefault()}
        >
          <Text className={cn(PaletteUtils.isVcfcPalette && 'flex items-center')} size='sm' color='secondary'>
            {PaletteUtils.isVcfcPalette && <Icon icon={<AttachmentIcon className='mr-2.5' width='16' height='16' />} />}
            <span className='mr-1'>{t('fileUpload.dropzone.drag')}</span>
            <span className={cn(PaletteUtils.isVcfcPalette && 'font-semibold text-primary')}>{t('fileUpload.dropzone.click')}</span>
          </Text>
          <input
            id={id}
            className='hidden'
            type='file'
            accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
            multiple={props.multiple}
            disabled={props.disabled}
            onChange={changeHandler}
          />
        </label>
      )}
      {hasFiles && (
        <div className='flex flex-col'>
          {props.multiple &&
            props.value?.map((file, idx) => <File key={`${idx}_${file.name}`} file={file} disabled={props.disabled} onDelete={props.onDelete} />)}
          {!props.multiple && <File file={props.value!} disabled={props.disabled} onDelete={props.onDelete} />}
        </div>
      )}
    </div>
  );
};

interface FileProps {
  className?: string;
  file: File | CustomFile;
  disabled?: boolean;
  onDelete?: (file: File | CustomFile) => void;
}

const File = (props: FileProps) => {
  return (
    <div className='flex items-center gap-2'>
      {props.file.name}
      <Button className='p-0' color='icon' onClick={props.disabled ? undefined : () => props.onDelete?.(props.file)}>
        <Icon className={cn('cursor-pointer', props.disabled && 'cursor-not-allowed')} icon={<CrossIcon />} />
      </Button>
    </div>
  );
};
